"use client";

import { useState, useMemo } from "react";
import Select from "react-select";
import ScheduleModal from "./ScheduleModal";
import { getTemplatesDetails } from "../../../services/templateServices";
import toast from "react-hot-toast";
import { sendSms } from "../../../services/sendSmsServices";
import whatsApp from "../../../assets/img/admin/whatsapp.png";
import ExcelModal from "./ExcelModal";
import { customStyles } from "../../../utils/index";

const MetaApiForm = ({ templates, groups, refreshSchedule }) => {
  const [sendTo, setSendTo] = useState("group");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [isSending, setIsSending] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [sampleMessage, setSampleMessage] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState({
    value: "",
    label: "",
    totalUsers: 0,
  });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedSendOption, setSelectedSendOption] = useState("schedule");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [mediaUrl, setMediaUrl] = useState("");
  const [templateType, setTemplateType] = useState("");

  const formattedScheduledTime = scheduleDate
    ? scheduleDate.toLocaleString("en-IN", {
        weekday: "long",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  const replaceTemplateParams = (message, params) => {
    let result = message;
    params.forEach((param) => {
      const valueToShow = param.value.trim() || `{${param.position}}`;
      const regex = new RegExp(`{{\\s*${param.position}\\s*}}`, "g");
      result = result.replace(regex, valueToShow);
    });
    return result;
  };

  const displayMessage = useMemo(() => {
    return sampleMessage
      ? replaceTemplateParams(sampleMessage, templateParams)
      : "";
  }, [sampleMessage, templateParams]);

  const groupOptions = groups.map((g) => ({
    value: g.value,
    label: g.label,
    totalUsers: g.totalUsers ?? 0,
  }));

  const templateOptions = templates.map((t) => ({
    value: t.value,
    label: t.label,
  }));

  const handleTemplateChange = async (option) => {
    setSelectedTemplate(option);
    if (!option?.value) return;

    try {
      const res = await getTemplatesDetails(option.value);
      const t = res.data;

      setTemplateName(t.templateName || "");
      setTemplateType(t.type || "text");
      setSampleMessage(t.sampleMessage || "");
      setMediaUrl(t.mediaUrl || "");

      if (Array.isArray(t.placeholders)) {
        const params = t.placeholders.map((p) => ({
          position: p.position,
          key: p.label,
          value: "",
        }));
        setTemplateParams(params);
      } else {
        setTemplateParams([]);
      }
    } catch (err) {
      toast.error("Failed to fetch template details");
      setTemplateName("");
      setSampleMessage("");
      setTemplateParams([]);
      setMediaUrl("");
    }
  };

  const handleParamChange = (index, value) => {
    const updated = [...templateParams];
    updated[index].value = value;
    setTemplateParams(updated);
  };

  const handleExcelDataSubmit = (data) => {
    setExcelData(data);
    setIsExcelModalOpen(false);
  };

  const handleSendViaMetaApi = async () => {
    if (!selectedTemplate?.value) return toast.error("Select template");

    setIsSending(true);

    try {
      const mode = selectedSendOption === "schedule" ? "schedule" : "send";
      const scheduledAt =
        selectedSendOption === "schedule"
          ? scheduleDate?.toISOString()
          : undefined;

      const buildBodyValues = () =>
        templateParams.reduce((acc, cur) => {
          if (cur.value) {
            acc[cur.position] = cur.value;
          }
          return acc;
        }, {});

      if (sendTo === "individual") {
        if (!phone) {
          toast.error("Enter phone number");
          setIsSending(false);
          return;
        }

        const individualBodyValues = buildBodyValues();

        const headerPayload = {
          headerFileName: templateName,
          headerData: mediaUrl,
          ...(Object.keys(individualBodyValues).length
            ? { bodyValues: individualBodyValues }
            : {}),
        };

        const payload = {
          wid: selectedTemplate.value,
          template_type: templateType,
          country_code: "91",
          version: "2.0",
          mode,
          scheduledAt,
          mobile: phone,
          userName: name,
          ...(templateType === "media" ? headerPayload : {}),
          ...(templateType !== "media" &&
          Object.keys(individualBodyValues).length
            ? {
                data: [
                  {
                    mobile: phone,
                    bodyValues: individualBodyValues,
                  },
                ],
              }
            : {}),
        };

        await sendSms(payload);
        await refreshSchedule();
        toast.success("Message sent successfully!");
        setSelectedTemplate(null);
        setTemplateName("");
        setSampleMessage("");
        setTemplateParams([]);
        setSelectedGroup({ value: "", label: "", totalUsers: 0 });
        setName("");
        setPhone("");
        setSelectedSendOption("schedule");
        setScheduleDate(null);
        setExcelData([]);
        setMediaUrl("");
        setTemplateType("");
      }
      if (sendTo === "group") {
        const group = groups.find((g) => g.value === selectedGroup.value);
        const users = excelData.length ? excelData : group?.users || [];

        if (!users.length) {
          toast.error("No users to send message");
          setIsSending(false);
          return;
        }

        const buildBulkData = () =>
          users.map((user) => {
            const mobile = user.phone || user.mobile;

            const bodyValues = templateParams.reduce((acc, param) => {
              const value = user[param.key] || param.value || "";
              if (value) {
                acc[param.position.toString()] = value;
              }
              return acc;
            }, {});

            const mediaHeader =
              templateType === "media"
                ? { headerValues: { headerData: mediaUrl } }
                : {};

            return {
              mobile,
              ...mediaHeader,
              ...(Object.keys(bodyValues).length ? { bodyValues } : {}),
            };
          });

        const payload = {
          version: "2.0",
          country_code: "91",
          wid: selectedTemplate.value,
          template_type: templateType,
          groupName: group?.label,
          mode,
          scheduledAt,
          data: buildBulkData(),
        };

        console.log(payload);

        await sendSms(payload);
        await refreshSchedule();
        toast.success(`Messages sent to ${users.length} users successfully!`);
        setSelectedTemplate(null);
        setTemplateName("");
        setSampleMessage("");
        setTemplateParams([]);
        setSelectedGroup({ value: "", label: "", totalUsers: 0 });
        setName("");
        setPhone("");
        setSelectedSendOption("schedule");
        setScheduleDate(null);
        setExcelData([]);
        setMediaUrl("");
        setTemplateType("");
      }
    } catch (err) {
      toast.error(err.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const filtered = groups.find((g) => g.value === selectedGroup.value);

  return (
    <>
      {isSending && <div>Loading ...</div>}
      <div
        style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
        className="max-w-lg w-full bg-white p-4 mt-4 rounded-2xl border border-gray-200 text-base font-medium text-black"
      >
        {/* Send To Type */}
        <div className="flex mb-4 justify-normal flex-col items-start relative w-full">
          <label className="block text-base font-semibold mb-3">Send to</label>
          <div className="flex gap-5">
            {["group", "individual"].map((val) => (
              <label
                key={val}
                className="flex items-center cursor-pointer relative"
              >
                <input
                  type="radio"
                  name="group"
                  value={val}
                  checked={sendTo === val}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="appearance-none sr-only"
                />
                <div className="w-5 h-5 absolute left-0 rounded-full bg-[#d9d9d9] flex items-center justify-center">
                  <div
                    className={`w-3 h-3 rounded-full transition ${
                      sendTo === val ? "bg-[#4F22D8]" : "bg-[#C0C4D1]"
                    }`}
                  />
                </div>
                <span className="ml-2 text-sm font-medium text-black">
                  {val === "individual" ? "By Individual" : "By Group"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Group Select */}
        {sendTo === "group" && (
          <div className="mb-4">
            <Select
              options={groupOptions}
              value={selectedGroup.value ? selectedGroup : null}
              onChange={(option) => setSelectedGroup(option)}
              styles={customStyles}
              components={{ IndicatorSeparator: () => null }}
              placeholder="Select a group"
            />
            {filtered && (
              <p className="text-xs text-black font-normal mt-1">
                This group contains {filtered.totalUsers ?? 0} users{" "}
              </p>
            )}
          </div>
        )}

        {/* Individual Inputs */}
        {sendTo === "individual" && (
          <div className="space-y-3 mb-4">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 h-[40px] border border-[#0000003D]/[24%] placeholder:text-black rounded-lg text-sm focus:outline-none bg-[#FAFAFA] focus:ring-1 focus:ring-blue"
            />
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <img src={whatsApp} alt="whatsapp" className="w-5 h-5" />
                <div className="h-6 w-px bg-gray-300"></div>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number "
                className="w-full pl-12 pr-3 h-[40px] placeholder:text-black border border-[#0000003D]/[24%] rounded-lg text-sm focus:outline-none bg-[#FAFAFA] focus:ring-1 focus:ring-blue"
              />
            </div>
          </div>
        )}

        {/* Template Selection */}
        <div className="border border-[#E0E0E0] rounded-[10px] p-3 mb-4">
          <label className="block text-sm mb-2">
            Select Meta Approved Template
          </label>
          <Select
            options={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateChange}
            styles={customStyles}
            components={{ IndicatorSeparator: () => null }}
            placeholder="Search or select template"
          />

          {templateType === "media" && mediaUrl && (
            <div className="mt-4">
              <label className="block text-sm mb-2">Media URL</label>
              <img
                src={mediaUrl}
                className="w-[100px] border rounded-[10px]"
                alt="media"
              />
            </div>
          )}

          {sampleMessage && (
            <div className="mt-4">
              <label className="block text-sm mb-2">Sample Message</label>
              <textarea
                rows={4}
                value={displayMessage}
                readOnly
                className="w-full border border-[#0000003D] bg-[#FAFAFA] rounded-[10px] text-black font-normal px-3 py-[10px] text-sm"
              />
            </div>
          )}

          {/* Template Params for both types */}
          {templateParams.length > 0 && sendTo === "individual" && (
            <div className="mt-4">
              <label className="block text-sm mb-2">
                Fill Template Parameters ({templateType})
              </label>
              <div className="space-y-3">
                {templateParams.map((param, index) => (
                  <div key={param.key} className="flex flex-col">
                    <label className="text-sm font-medium mb-1">
                      Varibale position {param.position} label {param.key}
                    </label>
                    <input
                      type="text"
                      value={param.value}
                      onChange={(e) => handleParamChange(index, e.target.value)}
                      placeholder={`Enter value for ${param.key}`}
                      className="border border-[#0000003D] bg-white rounded-[10px] text-black font-normal px-3 h-10 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {templateParams.length > 0 && sendTo === "group" && selectedGroup && (
            <div className="mt-4">
              <button
                onClick={() => setIsExcelModalOpen(true)}
                className="w-full bg-[#4F22D8] text-white font-semibold py-2 rounded-md  transition"
              >
                Fill Parameters via Excel
              </button>
              {excelData.length > 0 && (
                <p className="text-xs text-green mt-2">
                  Excel data loaded for {excelData.length} users
                </p>
              )}
            </div>
          )}
        </div>

        {/* Schedule Section */}
        <div className="mb-4">
          <label className="block text-base font-semibold mb-3">
            Schedule / Send
          </label>

          <div className="flex gap-6">
            {["Schedule For Later", "Send Now"].map((val) => {
              const valueKey =
                val === "Schedule For Later" ? "schedule" : "now";
              return (
                <label
                  key={valueKey}
                  className="flex items-center cursor-pointer relative"
                >
                  <input
                    type="radio"
                    name="group"
                    value={valueKey}
                    checked={selectedSendOption === valueKey}
                    onChange={(e) => setSelectedSendOption(e.target.value)}
                    className="appearance-none sr-only"
                  />
                  <div className="w-5 h-5 absolute left-0 rounded-full bg-[#d9d9d9] flex items-center justify-center">
                    <div
                      className={`w-3 h-3 rounded-full transition ${
                        selectedSendOption === valueKey
                          ? "bg-[#4F22D8]"
                          : "bg-[#C0C4D1]"
                      }`}
                    />
                  </div>
                  <span className="ml-2 text-sm font-medium text-black">
                    {val === "Schedule For Later" ? "Schedule" : "Send Now"}
                  </span>
                </label>
              );
            })}
          </div>

          {selectedSendOption === "schedule" && (
            <div
              className="mt-4 text-xs text-[#4F22D8] font-medium cursor-pointer"
              onClick={() => setIsScheduleModalOpen(true)}
            >
              {scheduleDate
                ? `Message Scheduled On ${formattedScheduledTime}`
                : "Select Date & Time"}
            </div>
          )}
        </div>

        {/* Final Submit */}
        <button
          onClick={handleSendViaMetaApi}
          disabled={
            isSending ||
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !phone)
          }
          className={`w-full bg-[#4F22D8] text-white font-semibold py-2 rounded-md transition ${
            isSending ||
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !phone)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#3b51a3] cursor-pointer"
          }`}
        >
          {isSending
            ? selectedSendOption === "schedule"
              ? "Scheduling..."
              : "Sending..."
            : selectedSendOption === "schedule"
            ? "Schedule Via Meta API"
            : "Send Via Meta API"}
        </button>
      </div>

      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onConfirm={(date) => {
          setScheduleDate(date);
          setIsScheduleModalOpen(false);
        }}
        initialDate={scheduleDate}
      />

      <ExcelModal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
        onSubmit={handleExcelDataSubmit}
        templateParams={templateParams}
        groupUsers={filtered?.users || []}
        setTemplateParams={setTemplateParams}
      />
    </>
  );
};

export default MetaApiForm;
